import React, { useState, useEffect, Fragment } from "react";
import { Button, Container, Grid, Paper, Typography, Card, Box, CardContent, IconButton } from "@mui/material";
import { useHistory } from "react-router-dom";
import queryString from "query-string";
import {
    RequestPatientQuestionnaireInfo,
    RequestPatientSatisfactionLink,
    GetQuestionnaireFaqs,
} from "../../Requests/contactingAPIs";
import { CHUIProgressBackdrop, useCHUIDarkModeContext } from "@connecthealth-ui/core";
import { SingleSelectDataGrid } from "./singleSelectDataGrid";
import { CountdownCircleTimer } from "react-countdown-circle-timer";
import useBreakpoints from "../../Utils/Hooks/useBreakPoint";
import { FaqCollapsible } from "./Faq/Faqs";
import { LiveHelp } from "@mui/icons-material";

export function QuestionnairePortal(props) {
    const { push } = useHistory();
    const [questionnaireDetails, setQuestionnaireDetails] = useState({});
    const [questionnaireTypeMap, setQuestionnaireTypeMap] = useState({});

    const [questionnairesLeft, setQuestionnairesLeft] = useState(0);
    const [questionnairesLeftCSS, setQuestionnairesLeftCSS] = useState(0);
    const [questionnairesTotalTime, setQuestionnairesTotalTime] = useState(0);
    const [nextDueDaysDuration, setNextDueDaysDuration] = useState(0);
    const [nextDueRemainingTime, setNextDueRemainingTime] = useState(0);
    const [refreshTimer, setRefreshTimer] = useState(0);
    const [faqData, setFaqData] = useState({});

    const [loading, setLoading] = useState(true);
    const [feedbackUrl, setFeedbackUrl] = useState("");
    const { prefersDarkMode } = useCHUIDarkModeContext();

    var faqContainer = document.getElementById("faqContainer");
    const scrollToFaq = () =>
        window.scrollTo({
            top: faqContainer.offsetTop,
            left: 0,
            behavior: "smooth",
        });

    //Get uuid
    const value = queryString.parse(window.location.search);
    const uuid = value.UUID;

    useEffect(() => {
        document.title = "Connect Health | Questionnaires";
    }, []);

    useEffect(async () => {
        let retrievedFaqData = await GetQuestionnaireFaqs();

        if (
            retrievedFaqData.data.data == null ||
            retrievedFaqData.status != "200" ||
            retrievedFaqData.data.statusCode != "200"
        ) {
            setFaqData({});
            return;
        }

        setFaqData(retrievedFaqData.data.data);
    }, []);

    const getDetails = async () => {
        //Get questionnaire list details
        let details = await RequestPatientQuestionnaireInfo(uuid);

        if (!details) return;

        //provide unique ID and secondsLeft
        details.forEach((questionnaire, i) => {
            questionnaire.portalUuid = uuid;
            questionnaire.id = i;
            questionnaire.secondsLeft =
                new Date(questionnaire.dateTimeDue).getTime() / 1000 - new Date().getTime() / 1000 > 0;
        });

        //check if all mandatory questionnaires are answered
        details.forEach((questionnaire, i) => {
            if (questionnaire.mandatory && questionnaire.completed == false && questionnaire.secondsLeft > 0) {
                //hide none mandatory questionnaires
                details.map((questionnaire, i) => {
                    if (questionnaire.mandatory == false) {
                        if (i > -1) {
                            details.splice(i, 1);
                        }
                    }
                });
            }
        });

        //calculate when the next questionnaire is due, for timer
        let lowestDueEndTime = 999999999999999;
        let dueEndTime = 0;
        let dueStartTime = 0;
        let questionnairesCompleted = 0;
        let questionnairesTotal = 0;
        let questionnairesEstTimeTotal = 0;

        details.forEach((element) => {
            if (new Date() / 1000 - new Date(element["dateTimeDue"]) / 1000 < 0) {
                if (lowestDueEndTime > new Date(element["dateTimeDue"]) / 1000) {
                    lowestDueEndTime = new Date(element["dateTimeDue"]) / 1000;
                    dueEndTime = new Date(element["dateTimeDue"]) / 1000;
                    dueStartTime = new Date(element["dateTimeStarted"]) / 1000;
                }
            }

            if (lowestDueEndTime == 999999999999999) {
                lowestDueEndTime = 0;
                dueEndTime = Date.now() / 1000;
            }

            if (element["completed"] || !element["secondsLeft"]) {
                questionnairesCompleted++;
            } else {
                questionnairesEstTimeTotal += element["duration"];
            }

            questionnairesTotal++;
        });

        setQuestionnairesLeft(questionnairesTotal - questionnairesCompleted);
        let percentageComplete = (questionnairesCompleted / questionnairesTotal) * 100;

        setQuestionnairesLeftCSS(
            `linear-gradient(0deg, #27ae60 ${percentageComplete}%, #f5f8fc ${percentageComplete + 1}%, #f5f8fc ${
                100 - percentageComplete
            }%)`
        );
        setQuestionnairesTotalTime(questionnairesEstTimeTotal);

        let remainingTime = dueEndTime - Date.now() / 1000;
        let timeSinceStart = dueEndTime - dueStartTime;
        const days = Math.ceil(remainingTime / 86400);

        if (remainingTime < 0) {
            remainingTime = 0;
            timeSinceStart = 0;
        }

        setNextDueDaysDuration(timeSinceStart);
        setNextDueRemainingTime(remainingTime);

        setQuestionnaireTypeMap(questionnaireTypeMap);
        setQuestionnaireDetails(details);
        setLoading(false);
    };

    useEffect(() => {
        document.title = "Connect Health | Questionnaires";
    }, []);

    useEffect(async () => {
        let feedbackUrl = await RequestPatientSatisfactionLink(uuid);
        setFeedbackUrl(feedbackUrl);
        getDetails();

        // setInterval(() => {
        //     getDetails();
        //     reset();
        // }, 30000);
    }, []);

    const tick = () => {
        if (refreshTimer == 0) {
            reset();
        } else {
            setRefreshTimer(refreshTimer - 1);
        }
    };

    const reset = () => setRefreshTimer(30);

    useEffect(() => {
        const timerId = setInterval(() => tick(), 1000);
        return () => clearInterval(timerId);
    });

    //functions for timer
    const renderTime = (dimension, time) => {
        return (
            <div className="time-wrapper">
                <div className="time">{time}</div>
                <div>{dimension}</div>
            </div>
        );
    };
    const getTimeDays = (time) => (time / 86400) | 0;
    const getTimeHours = (time) => ((time % 86400) / 3600) | 0;

    const breakPoint = useBreakpoints();
    let containerPaddingSides = breakPoint == "lg" || breakPoint == "md" || breakPoint == "smd" ? 16 : 0;

    if (loading) {
        return <CHUIProgressBackdrop color="secondary" />;
    } else {
        return (
            <Fragment>
                <Box
                    sx={{
                        paddingBottom: {
                            xs: 5,
                            md: 8,
                        },
                        background: "#fcfdff",
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            textAlign: "center",
                            padding: 3,
                        }}
                    ></Typography>
                    <Container
                        maxWidth="md"
                        sx={{
                            padding: `${containerPaddingSides} ${containerPaddingSides} 40px ${containerPaddingSides}`,
                        }}
                    >
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <Card
                                    small
                                    sx={{
                                        height: "155px",
                                        backgroundColor: "#f5f8fc",

                                        boxShadow: " rgba(0, 0, 0, 0.24) 0px 3px 8px",
                                    }}
                                >
                                    <CardContent sx={{ height: "155px", background: questionnairesLeftCSS }}>
                                        <Typography align="center" sx={{ font: "16px" }}>
                                            Questionnaires left
                                        </Typography>
                                        <Typography align="center" sx={{ fontSize: "42px" }}>
                                            {questionnairesLeft}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid
                                item
                                sm={6}
                                md={4}
                                sx={{
                                    display: { xs: "none", sm: "block" },
                                }}
                            >
                                <Card
                                    small
                                    sx={{
                                        height: "155px",
                                        backgroundColor: "#f5f8fc",
                                        boxShadow: " rgba(0, 0, 0, 0.24) 0px 3px 8px",
                                    }}
                                >
                                    <CardContent sx={{ backgroundColor: "#f5f8fc" }}>
                                        <Typography align="center" sx={{ font: "16px", color: "#585a5c" }}>
                                            Est time to complete
                                        </Typography>
                                        <Typography align="center" sx={{ fontSize: "42px" }}>
                                            {questionnairesTotalTime} Mins
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid
                                item
                                md={4}
                                sx={{
                                    display: { xs: "none", md: "block" },
                                }}
                            >
                                <Card
                                    small
                                    sx={{
                                        height: "155px",
                                        backgroundColor: "#f5f8fc",
                                        boxShadow: " rgba(0, 0, 0, 0.24) 0px 3px 8px",
                                    }}
                                >
                                    <CardContent align="center" sx={{ backgroundColor: "#f5f8fc" }}>
                                        <Typography
                                            align="center"
                                            sx={{ font: "16px", color: "#585a5c", marginBottom: "10px" }}
                                        >
                                            Next Questionnaire Due
                                        </Typography>
                                        <div>
                                            <CountdownCircleTimer
                                                isPlaying
                                                strokeWidth={4}
                                                size={80}
                                                colors={[
                                                    ["#27ae60", 0.4],
                                                    ["#f5752f", 0.4],
                                                    ["#cc0000", 0.2],
                                                ]}
                                                duration={nextDueDaysDuration}
                                                initialRemainingTime={nextDueRemainingTime}
                                            >
                                                {({ elapsedTime }) =>
                                                    nextDueRemainingTime > 0
                                                        ? getTimeDays(nextDueDaysDuration - elapsedTime) == 0
                                                            ? renderTime(
                                                                  "hours",
                                                                  getTimeHours(nextDueDaysDuration - elapsedTime)
                                                              )
                                                            : renderTime(
                                                                  "days",
                                                                  getTimeDays(nextDueDaysDuration - elapsedTime)
                                                              )
                                                        : 0 + " hours"
                                                }
                                            </CountdownCircleTimer>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        <Paper
                            sx={{
                                marginTop: 3,
                                padding: 1,
                                boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
                            }}
                            elevation={prefersDarkMode ? 2 : 1}
                        >
                            <Grid container>
                                <Grid item xs={6} sx={{ justifyContent: "flex-start", display: "flex" }}>
                                    {/* <Typography>
                                        Refresh in: <strong>~{refreshTimer}s</strong>
                                    </Typography> */}
                                </Grid>
                                <Grid item xs={6} sx={{ justifyContent: "flex-end", display: "flex" }}>
                                    <Box sx={{ height: "40px", padding: "4px 8px 0px 0px" }}>
                                        <Button
                                            sx={{
                                                height: "40px",
                                                background: "#0081C9",
                                                fontWeight: "bold",
                                                color: "#fff",
                                                transition: "all 0.3s ease-in-out",
                                                ":hover": {
                                                    marginTop: "-3px",
                                                    background: "#2ea6e8",
                                                    color: "#ffece3",
                                                },
                                            }}
                                            onClick={scrollToFaq}
                                        >
                                            <LiveHelp /> <Box sx={{ width: "8px" }} /> FAQ
                                        </Button>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography
                                        sx={{ marginBottom: 2.5 }}
                                        align="center"
                                        variant={"h4"}
                                        htmlFor="questionnairesList"
                                        tabIndex="0"
                                    >
                                        Questionnaires
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography>
                                        <SingleSelectDataGrid
                                            push={push}
                                            rows={questionnaireDetails}
                                            questionnaireTypeMap={questionnaireTypeMap}
                                            name={"questionnairesList"}
                                        />
                                    </Typography>
                                </Grid>
                            </Grid>

                            {/* <Grid container spacing={3} justifyContent="center" alignItems="center" sx={{marginTop: 4}}>
                            <Grid item xs={12}>
                                <Typography variant="h6" align="center" >
                                    Click here to provide anonymous feedback. 
                                </Typography>
                            </Grid>
                            <Box height={30}>
                                <Button 
                                    align="center"
                                    variant="contained"
                                    size="large"
                                    sx={{
                                            background: "#F5F5F5",
                                            border: "1px solid #FCFCFC",
                                            boxShadow: "1px 1px 2px rgba(#FCFCFC, .2)",
                                            fontWeight: 900,
                                            letterSpacing: "1px",
                                            transition: "all 150ms linear",
                                            color: "#585a5c",
                                        
                                        ":hover": {
                                            backgroundColor: `#fafafa`,
                                            border: "#f5f5f5",
                                            boxShadow: "0px 2px 0 #E6E6E6, 2px 4px 6px #F5F5F5",
                                            color: "#787c80",
                                            textDecoration: "none",
                                            textShadow: "-1px -1px 0 #F5F5F5",
                                            transition: "all 250ms linear",
                                        }
                                    }}
                                    onClick={async () => window.open(feedbackUrl)}
                                >
                                    <strong>Feedback</strong>
                                </Button>
                            </Box>
                        </Grid> */}
                        </Paper>
                    </Container>

                    <FaqCollapsible faqData={faqData} />
                </Box>
            </Fragment>
        );
    }
}
